import {
  ESTADO_CONTACTO,
  agruparClientesPorEmpresa,
  determinarEstadoContacto,
  normalizarEmpresa,
} from "../../../utils/empresaContacto";

// Las 7 categorías vigentes que Ignacio sigue — nombres EXACTOS de `categorias.nombre` en la base
// (fuente única del recorte, 10.1 — nunca reimplementar esta lista en otra pantalla).
export const CATEGORIAS_MAPA = [
  "AUTOMOTRIZ",
  "BANCA & FINANZAS",
  "TECNOLOGÍA",
  "COSMÉTICOS & PERFUMES",
  "VESTIMENTA & ACCESORIOS",
  "TELCOM",
  "EDUCACIÓN",
];

const LABEL_FILA = {
  AUTOMOTRIZ: "Automotriz",
  "BANCA & FINANZAS": "Banca & Finanzas",
  TECNOLOGÍA: "Tecnología",
  "COSMÉTICOS & PERFUMES": "Cosméticos & Perfumes",
  "VESTIMENTA & ACCESORIOS": "Vestimenta & Accesorios",
  TELCOM: "Telcom",
  EDUCACIÓN: "Educación",
  VIP: "VIP",
  OTROS: "Otros",
};

const ORDEN_FILAS_BASE = [...CATEGORIAS_MAPA, "VIP", "OTROS"];

/**
 * Resuelve a qué fila(s) del mapa pertenece una fila de `clientes` (granulada por marca). Regla
 * única: la categoría la aporta la FILA, VIP la aporta la EMPRESA. Una fila de `clientes` puede
 * pertenecer a 1 o 2 filas del mapa — nunca a más de 2 (su categoría propia + VIP).
 *   - Empresa VIP: SIEMPRE va a 'VIP'. Si además su categoría es una de las 7, TAMBIÉN cuenta ahí.
 *   - Empresa NO VIP: va a su categoría si es una de las 7; si no, a 'OTROS'.
 * Consecuencia (declarada en pantalla, letra chica): el total de empresas del mapa NO es la suma
 * de las filas — una empresa VIP con marca en una de las 7 categorías cuenta en dos.
 * @param {string|null} categoriaNombre
 * @param {boolean} esVip
 * @returns {string[]}
 */
export const resolverFilasDeClienteRow = (categoriaNombre, esVip) => {
  const filas = [];
  if (esVip) filas.push("VIP");
  if (CATEGORIAS_MAPA.includes(categoriaNombre)) {
    filas.push(categoriaNombre);
  } else if (!esVip) {
    filas.push("OTROS");
  }
  return filas;
};

/** Arma, para cada fila del mapa, el array de filas de `clientes` que le pertenecen. */
const construirMembresiaPorFila = (clientes, categoriasPorId, empresasVipSet) => {
  const porFila = new Map(ORDEN_FILAS_BASE.map((f) => [f, []]));
  clientes.forEach((c) => {
    const categoriaNombre = categoriasPorId.get(c.categoria) || null;
    const empresaNorm = normalizarEmpresa(c.empresa);
    const esVip = empresaNorm !== "" && empresasVipSet.has(empresaNorm);
    resolverFilasDeClienteRow(categoriaNombre, esVip).forEach((filaKey) => porFila.get(filaKey).push(c));
  });
  return porFila;
};

const esCierre = (op) => op.estatus === "100%";

/**
 * Núcleo de la vista Categorías: arma los datos de los dos paneles (evolución + cobertura) a
 * partir de datos YA cargados (cargarOportunidadesVentas + getActividadPrecalculada +
 * empresas_vip) — sin queries nuevas, sin recalcular fuentes ajenas (10.1/10.27).
 *
 * @param {object} args
 * @param {Array} args.clientes - filas de `clientes` del país activo (no eliminadas)
 * @param {Array} args.oportunidades - de cargarOportunidadesVentas (sales+contratos+btl)
 * @param {Array} args.categorias - [{id, nombre}]
 * @param {Set<string>} args.empresasVipSet - empresa_norm de empresas_vip para este país
 * @param {Set<number>} args.clienteIdsConActividadReciente - cliente_id con actividad CRM <=90d
 * @param {string[]} args.mesesDelPeriodo - 12 claves "MM-YYYY", orden cronológico
 * @param {(op: object) => number} args.getImporteLocal - conversor de moneda (10.185)
 * @returns {{filasEvolucion: Array, filasCobertura: Array}}
 */
export const construirDatosCategorias = ({
  clientes,
  oportunidades,
  categorias,
  empresasVipSet,
  clienteIdsConActividadReciente,
  mesesDelPeriodo,
  getImporteLocal,
}) => {
  const categoriasPorId = new Map((categorias || []).map((c) => [c.id, c.nombre]));
  const clientesPorId = new Map((clientes || []).map((c) => [c.id, c]));
  const mesesSet = new Set(mesesDelPeriodo);

  const membresiaPorFila = construirMembresiaPorFila(clientes || [], categoriasPorId, empresasVipSet);

  // Estado GLOBAL de cada empresa (activado/vinculado/sin_contacto) — se calcula UNA vez por
  // empresa, sobre TODAS sus filas-marca (cualquier categoría), nunca acotado a una fila del mapa:
  // una empresa activa por una marca no aparece "sin contacto" en la fila de otra marca suya.
  const gruposEmpresaTotal = agruparClientesPorEmpresa(clientes || []);
  const clienteIdsConCierre = new Set(
    (oportunidades || []).filter((op) => esCierre(op) && mesesSet.has(op.mes_cierre)).map((op) => op.client_id)
  );
  const estadoPorEmpresa = new Map();
  gruposEmpresaTotal.forEach((grupo, empresaNorm) => {
    const tieneVentaCerradaEnPeriodo = grupo.clienteIds.some((id) => clienteIdsConCierre.has(id));
    const tieneActividadReciente = grupo.clienteIds.some((id) => clienteIdsConActividadReciente.has(id));
    estadoPorEmpresa.set(empresaNorm, determinarEstadoContacto({ tieneVentaCerradaEnPeriodo, tieneActividadReciente }));
  });

  // ── Panel de evolución (tira de calor) ────────────────────────────────────────────────────
  const oportunidadesPorFila = new Map(ORDEN_FILAS_BASE.map((f) => [f, []]));
  (oportunidades || []).forEach((op) => {
    if (!esCierre(op) || !mesesSet.has(op.mes_cierre)) return;
    const clienteRow = clientesPorId.get(op.client_id);
    if (!clienteRow) return;
    const categoriaNombre = categoriasPorId.get(clienteRow.categoria) || null;
    const empresaNorm = normalizarEmpresa(clienteRow.empresa);
    const esVip = empresaNorm !== "" && empresasVipSet.has(empresaNorm);
    const empresaDisplay = (clienteRow.empresa || "").trim();
    resolverFilasDeClienteRow(categoriaNombre, esVip).forEach((filaKey) =>
      oportunidadesPorFila.get(filaKey).push({ op, empresaNorm, empresaDisplay })
    );
  });

  const armarFila = (filaKey, opsDeLaFila) => {
    const celdasPorMes = new Map(mesesDelPeriodo.map((m) => [m, 0]));
    const montoPorEmpresa = new Map();
    opsDeLaFila.forEach(({ op, empresaNorm, empresaDisplay }) => {
      const monto = getImporteLocal(op);
      celdasPorMes.set(op.mes_cierre, (celdasPorMes.get(op.mes_cierre) || 0) + monto);
      if (empresaNorm) {
        const prev = montoPorEmpresa.get(empresaNorm) || { empresaDisplay, monto: 0 };
        prev.monto += monto;
        montoPorEmpresa.set(empresaNorm, prev);
      }
    });
    const celdas = mesesDelPeriodo.map((mes) => ({ mes, monto: celdasPorMes.get(mes) || 0 }));
    const totalPeriodo = celdas.reduce((acc, c) => acc + c.monto, 0);
    const mesesActivos = celdas.filter((c) => c.monto > 0).length;
    let empresaTop = null;
    let montoTop = 0;
    montoPorEmpresa.forEach((v) => {
      if (v.monto > montoTop) {
        montoTop = v.monto;
        empresaTop = v.empresaDisplay;
      }
    });
    const concentracionPct = totalPeriodo > 0 ? (montoTop / totalPeriodo) * 100 : 0;
    return {
      filaKey,
      label: LABEL_FILA[filaKey] || filaKey,
      celdas,
      totalPeriodo,
      empresasDistintas: montoPorEmpresa.size,
      mesesActivos,
      concentracionPct,
      empresaConcentracion: empresaTop,
    };
  };

  const filasArmadas = ORDEN_FILAS_BASE.map((filaKey) => armarFila(filaKey, oportunidadesPorFila.get(filaKey)));

  // Orden fijo (2026-09-23, pedido de Ignacio): las 7 categorías por monto descendente, después
  // Otros, y VIP siempre al final — VIP y Otros NUNCA se ordenan por monto entre sí ni contra las
  // categorías. Sin despliegue por empresa (retirado — la fila VIP ya no se abre).
  const filasCategorias = filasArmadas
    .filter((f) => f.filaKey !== "VIP" && f.filaKey !== "OTROS")
    .sort((a, b) => b.totalPeriodo - a.totalPeriodo);
  const filaOtros = filasArmadas.find((f) => f.filaKey === "OTROS");
  const filaVip = filasArmadas.find((f) => f.filaKey === "VIP");
  const filasEvolucion = [...filasCategorias, filaOtros, filaVip].filter(Boolean);

  // ── Panel de cobertura ─────────────────────────────────────────────────────────────────────
  const filasCobertura = ORDEN_FILAS_BASE.map((filaKey) => {
    const clientesDeLaFila = membresiaPorFila.get(filaKey);
    const gruposEmpresaFila = agruparClientesPorEmpresa(clientesDeLaFila);
    const porEstado = { activado: [], vinculado: [], sin_contacto: [] };
    gruposEmpresaFila.forEach((grupo, empresaNorm) => {
      const estado = estadoPorEmpresa.get(empresaNorm) || ESTADO_CONTACTO.SIN_CONTACTO;
      porEstado[estado].push(grupo.empresaDisplay);
    });
    Object.values(porEstado).forEach((arr) => arr.sort((a, b) => a.localeCompare(b)));
    const total = gruposEmpresaFila.size;
    return {
      filaKey,
      label: LABEL_FILA[filaKey] || filaKey,
      total,
      activado: porEstado.activado.length,
      vinculado: porEstado.vinculado.length,
      sinContacto: porEstado.sin_contacto.length,
      activadoPct: total > 0 ? (porEstado.activado.length / total) * 100 : 0,
      nombresPorEstado: porEstado,
    };
  });

  // Mismo orden que la evolución — las dos filas de una misma categoría quedan a la misma altura
  // en los dos paneles.
  const ordenFilas = filasEvolucion.map((f) => f.filaKey);
  filasCobertura.sort((a, b) => ordenFilas.indexOf(a.filaKey) - ordenFilas.indexOf(b.filaKey));

  return { filasEvolucion, filasCobertura };
};
